import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { UnitsInfoContainer } from 'pages/NewOverview/components/Loading/styles';
import { LoadingUnitsInfo } from 'pages/NewOverview/components/Loading/UnitsInfo';
import { ThermalComfortUnitCard } from './Card';
import { useThermalComfortDataContext } from 'pages/NewOverview/contexts/ThermalComfortDataContext';
import { useHistory } from 'react-router-dom';
import { handleFilterStateRoute } from 'pages/NewOverview/helpers/filters';
import { getUserProfile } from 'helpers/userProfile';
import { useFilterContext } from 'pages/NewOverview/contexts/FilterContext';
import { ThermalComfortVariant } from './types';
import { canSeeDetailsButtonByValue } from '../../helpers';

interface UnitsInfoProps {
  hasThermalComfort: boolean;
  loading: boolean;
}

export function UnitsInfo({
  hasThermalComfort,
  loading,
}: Readonly<UnitsInfoProps>): ReactElement {
  const [profile] = useState(getUserProfile());
  const { selectedOptions, clients } = useFilterContext();
  const history = useHistory();
  const {
    loading: loadingThermal, data, refetch, units,
  } = useThermalComfortDataContext();

  const generalLoading = useMemo(() => loading || (loadingThermal && hasThermalComfort), [hasThermalComfort, loading, loadingThermal]);

  const showValues = hasThermalComfort && !loading;

  function getUnitsIdsByVariant(variant: ThermalComfortVariant): string[] {
    return units
      .filter((unit) => unit.thermalComfort === variant)
      .map((unit) => unit.id.toString());
  }

  function onClickDetails(variant: ThermalComfortVariant): void {
    const filter = handleFilterStateRoute(clients, selectedOptions, profile);
    const unitIds = getUnitsIdsByVariant(variant);

    if (unitIds.length) {
      filter.units = unitIds;
    }

    history.push('/analise/unidades', filter);
  }

  useEffect(() => {
    if (hasThermalComfort) {
      const clientIds = clients.map(Number);
      refetch(clientIds, selectedOptions);
    }
  }, [
    selectedOptions.group?.units,
    selectedOptions.state,
    selectedOptions.status,
    selectedOptions.cities,
    selectedOptions.units,
  ]);

  if (generalLoading) {
    return (
      <LoadingUnitsInfo />
    );
  }

  return (
    <UnitsInfoContainer data-testId="thermal-confort-units-info">
      <ThermalComfortUnitCard
        variant="comfortable"
        value={showValues ? data?.comfortable : undefined}
        onClickDetails={() => onClickDetails('comfortable')}
        canSeeDetails={canSeeDetailsButtonByValue(showValues ? data?.comfortable : undefined)}
      />
      <ThermalComfortUnitCard
        variant="belowLimit"
        value={showValues ? data?.belowLimit : undefined}
        onClickDetails={() => onClickDetails('belowLimit')}
        canSeeDetails={canSeeDetailsButtonByValue(showValues ? data?.belowLimit : undefined)}
      />
      <ThermalComfortUnitCard
        variant="aboveLimit"
        value={showValues ? data?.aboveLimit : undefined}
        onClickDetails={() => onClickDetails('aboveLimit')}
        canSeeDetails={canSeeDetailsButtonByValue(showValues ? data?.aboveLimit : undefined)}
      />
      <ThermalComfortUnitCard
        variant="inoperative"
        value={showValues ? data?.inoperative : undefined}
        onClickDetails={() => onClickDetails('inoperative')}
        canSeeDetails={canSeeDetailsButtonByValue(showValues ? data?.inoperative : undefined)}
      />
    </UnitsInfoContainer>
  );
}
